import { logger, security } from '@beelday/common'
import { useDispatch, useSelector } from 'app-redux'
import {
	createClickerPresses,
	ProvideClickerPresses,
} from 'features/script/clicker-presses'
import {
	createContext,
	FC,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react'
import { useParams } from 'react-router-dom'
import type { MediaProgress } from 'room/common/media-progress'
import { ProvideStageSurface } from 'room/common/stage-surface'
import { selectTraining } from 'session-info/redux'
import { createConsoleEffects } from './console-effects'
import { BROWSER_CONSOLE_PORTS, ConsolePorts } from './console-ports'
import { PresentPath, presentPath } from './model/capabilities'
import { ConsoleState, isRunning } from './model/presentation-machine'
import { PresenterCommand } from './model/presenter-messages'
import { presenterActions } from './redux'
import type { Relay } from './replication/relay'
import { useBlackScreenKey } from './use-black-screen-key'
import { ProjectorOutbox, useConsoleLink } from './use-console-link'
import { usePresentationMachine } from './use-presentation-machine'
import { useScreenCapabilities } from './use-screen-capabilities'
import { useSpeakerChoice } from './use-speaker-choice'
import { isExtendedDisplay, isOwnFullscreen } from './window-management'

const log = logger.create('presenter-mode')

export type PresenterConsole = {
	state: ConsoleState
	/** What Present does right now. */
	path: PresentPath
	/**
	 * Present places the window on the projector by itself, so entering the
	 * room and presenting can be the same click.
	 */
	canPresentInOneClick: boolean
	present: () => void
	/** Full screen in this window, no presentation window. */
	presentHere: () => void
	/** Hands this click's right to go full screen to the presentation window. */
	showFullscreen: () => void
	end: () => void
	toggleBlank: () => void
	/** The speakers the recordings play through; `null` is the system default. */
	chooseSpeakers: (deviceId: string | null) => void
	/** The presentation window could not play through the chosen speakers. */
	speakersFellBack: boolean
}

const PresenterConsoleContext = createContext<PresenterConsole | null>(null)
const ConsoleMediaContext = createContext<MediaProgress | null>(null)
const nowhere = (): void => undefined
const ReloadMediaContext = createContext<() => void>(nowhere)

/** `null` unless this window can present (`ProvidePresenterConsole`). */
export const usePresenterConsole = (): PresenterConsole | null =>
	useContext(PresenterConsoleContext)

/** Where the presentation window's player is, while a presentation runs. */
export const useConsoleMedia = (): MediaProgress | null =>
	useContext(ConsoleMediaContext)

/**
 * Asks the presentation window's recording to load its file again, for when
 * the report of its player says it failed.
 */
export const useReloadProjectorMedia = (): (() => void) =>
	useContext(ReloadMediaContext)

/** The trainer's own window of a session whose running chunk is in person. */
const useCanPresent = (): boolean => {
	const training = useSelector(state => selectTraining(state.sessionInfo))
	const { id } = security.useAuthenticatedUser()
	return (
		training?.currentChunkSessionType === 'IN_PERSON' &&
		training.upperEchelon.userId === id
	)
}

type Props = {
	/** The store's relay (`window-store.ts`). */
	relay: Relay | null
	ports?: ConsolePorts
}

/**
 * Presenter mode in the trainer's main window - the console. Mounted around
 * the whole session route, so it outlives room changes: a break moves the
 * trainer to the lobby room, and the projector must keep showing.
 *
 * It composes the console's parts - the state machine and its effects, the
 * link to the presentation window, the screens - and provides the stage
 * surface, the clicker presses from the projector and the media progress to
 * the room below. For anyone who cannot present it provides nothing but the
 * ordinary `room` surface.
 */
export const ProvidePresenterConsole: FC<Props> = ({
	relay,
	ports = BROWSER_CONSOLE_PORTS,
	children,
}) => {
	const { workflowId: liveSessionId } = useParams<{ workflowId: string }>()
	const dispatch = useDispatch()
	const enabled = useCanPresent()
	const { capabilities, screens } = useScreenCapabilities(enabled)
	const [media, setMedia] = useState<MediaProgress | null>(null)
	const [speakersFellBack, setSpeakersFellBack] = useState(false)
	const presses = useMemo(createClickerPresses, [])
	const outbox: ProjectorOutbox = useRef(nowhere)

	const effects = useMemo(
		() =>
			createConsoleEffects({
				liveSessionId,
				ports,
				screens,
				toProjector: message => outbox.current(message),
				showBlank: blank => dispatch(presenterActions.blankShown(blank)),
				warn: (message, error) => log.warn(message, error),
			}),
		[dispatch, liveSessionId, ports, screens]
	)
	useEffect(() => () => effects.dispose(), [effects])

	const [state, send] = usePresentationMachine(effects.run)
	const running = isRunning(state) ? state : undefined
	const chooseSpeakers = useSpeakerChoice(running?.id)

	const onCommand = useCallback(
		(command: PresenterCommand) =>
			command === 'black'
				? send({ type: 'toggle-blank' })
				: presses.press(command),
		[presses, send]
	)
	useConsoleLink({
		liveSessionId,
		presentation: running?.id,
		relay,
		effects,
		send,
		outbox,
		onMedia: setMedia,
		onCommand,
		onSpeakers: setSpeakersFellBack,
	})

	// A console that reloaded while presenting picks its presentation up again;
	// one that may no longer present (the session went online) ends it.
	useEffect(() => {
		if (!enabled) {
			send({ type: 'end' })
			return
		}
		const saved = ports.storage.read(liveSessionId)
		if (saved) {
			send({ type: 'restored', saved })
		}
	}, [enabled, liveSessionId, ports, send])

	// Full screen in this window - one-screen presenting - starts and ends here.
	useEffect(() => {
		if (!enabled) {
			return
		}
		const onChange = (): void =>
			send({ type: 'fullscreen-here-changed', on: isOwnFullscreen() })
		document.addEventListener('fullscreenchange', onChange)
		return () => document.removeEventListener('fullscreenchange', onChange)
	}, [enabled, send])

	const runningId = running?.id
	useEffect(() => {
		setMedia(null)
		setSpeakersFellBack(false)
	}, [runningId])

	const toggleBlank = useCallback(() => send({ type: 'toggle-blank' }), [send])
	const reloadMedia = useCallback(
		() => outbox.current({ kind: 'reload-media' }),
		[]
	)
	useBlackScreenKey(running ? toggleBlank : undefined)

	const path = presentPath(capabilities)
	const value = useMemo<PresenterConsole | null>(
		() =>
			enabled
				? {
						state,
						path,
						canPresentInOneClick: path === 'place',
						// The display can change between renders (a projector
						// plugged in), so Present asks again.
						present: () =>
							send({
								type: 'present',
								path: presentPath({
									...capabilities,
									extended: isExtendedDisplay(),
								}),
							}),
						presentHere: () => send({ type: 'present-here' }),
						showFullscreen: () => send({ type: 'show-fullscreen' }),
						end: () => send({ type: 'end' }),
						toggleBlank,
						chooseSpeakers,
						speakersFellBack,
				  }
				: null,
		[
			capabilities,
			chooseSpeakers,
			enabled,
			path,
			send,
			speakersFellBack,
			state,
			toggleBlank,
		]
	)

	return (
		<PresenterConsoleContext.Provider value={value}>
			<ConsoleMediaContext.Provider value={running ? media : null}>
				<ReloadMediaContext.Provider value={reloadMedia}>
					<ProvideClickerPresses presses={presses}>
						<ProvideStageSurface surface={running ? 'console' : 'room'}>
							{children}
						</ProvideStageSurface>
					</ProvideClickerPresses>
				</ReloadMediaContext.Provider>
			</ConsoleMediaContext.Provider>
		</PresenterConsoleContext.Provider>
	)
}
